import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useTheme } from '@mui/material';
import MuiPopover from '@mui/material/Popover';
import { debounce } from 'essential-utils';
import * as React from 'react';
import { Box } from '../box';
import { reactElementSerializer, withVersion } from '../utils';
export * from './with-tooltip';
const PopoverImpl = React.forwardRef((props, ref) => {
    const { behaviour = 'click', hideBackdrop: _hideBackdrop, onClose, sx, slotProps } = props;
    const hideBackdrop = behaviour === 'hover' ? true : (_hideBackdrop ?? false);
    const { spacing } = useTheme();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const labelRef = React.useRef(null);
    const open = props.open ?? Boolean(anchorEl);
    const onOpen = (event) => {
        event.stopPropagation();
        if (!props.anchorEl) {
            setAnchorEl(event.currentTarget);
        }
        props.onOpen?.();
    };
    const onOpenDebounced = React.useMemo(() => {
        return debounce(onOpen, 300);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.onOpen, props.anchorEl]);
    const onCloseDebounced = React.useMemo(() => {
        return debounce(() => onClose?.(), 150);
    }, [onClose]);
    React.useEffect(() => {
        if (props.anchorEl) {
            setAnchorEl(props.anchorEl);
        }
    }, [props.anchorEl]);
    React.useEffect(() => {
        return () => {
            onOpenDebounced.cancel();
            onCloseDebounced.cancel();
        };
    }, [onOpenDebounced, onCloseDebounced]);
    const labelEnterEvent = (() => {
        switch (behaviour) {
            case 'click':
                return { onClick: props.disabled ? undefined : onOpen };
            case 'hover':
                return {
                    onMouseEnter: props.disabled
                        ? undefined
                        : onOpenDebounced
                };
        }
    })();
    const labelLeaveEvent = (() => {
        switch (behaviour) {
            case 'click':
                return {};
            case 'hover':
                return {
                    onMouseLeave: () => {
                        onOpenDebounced.cancel();
                        onCloseDebounced();
                    }
                };
        }
    })();
    const label = props.label
        ? React.cloneElement(props.label, {
            ref: labelRef,
            ...labelEnterEvent,
            ...labelLeaveEvent
        })
        : null;
    return (_jsxs(_Fragment, { children: [label, _jsx(MuiPopover, { ref: ref, hideBackdrop: hideBackdrop, anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'center',
                    ...props.anchorOrigin
                }, transformOrigin: {
                    vertical: props.transformOrigin?.vertical ??
                        parseInt(spacing(-0.5), 10),
                    horizontal: 'center',
                    ...props.transformOrigin
                }, anchorEl: anchorEl ?? labelRef.current, open: open, onClose: onClose, onMouseLeave: behaviour === 'hover' ? onCloseDebounced : undefined, onMouseEnter: () => {
                    onOpenDebounced.cancel();
                    onCloseDebounced.cancel();
                }, disableAutoFocus: props.disableAutoFocus, disableEnforceFocus: props.disableEnforceFocus, action: props.action, sx: {
                    ...(hideBackdrop
                        ? {
                            pointerEvents: 'none',
                            '> *': {
                                pointerEvents: 'auto'
                            }
                        }
                        : undefined),
                    ...sx
                }, slotProps: slotProps, children: props.children })] }));
});
export const Popover = withVersion(PopoverImpl, '1.0.0');
// FIXME: THis causes the tests to fail:
// TypeError: Cannot read properties of undefined (reading '__emotion_styles')
// const PopoverLabelWrapper = styled(Box)({ width: 'max-content' });
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function usePopoverProps(options) {
    const [open, setOpen] = React.useState(false);
    const stringifiedLabelProps = JSON.stringify(options.labelProps, reactElementSerializer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const Label = React.useMemo(() => options.label, [stringifiedLabelProps]);
    return {
        disabled: options.disabled,
        behaviour: options.behaviour,
        label: (_jsx(Box, { width: "max-content", ...options.labelWrapperProps, children: _jsx(Label, { ...options.labelProps, open: open, "aria-controls": open ? options['aria-label'] : undefined, "aria-expanded": open ? 'true' : undefined, "aria-haspopup": "true" }) })),
        open,
        onOpen: () => setOpen(true),
        onClose: () => setOpen(false)
    };
}
